import uniqueId from "lodash/uniqueId"
import React, { useMemo } from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { Icon } from "../Icon"
import type { RequiredSome } from "../types/util"
import { overloadColor } from "../util/style"
import { isUndefined } from "../util/typechecks"
import type { BreadcrumbsProps, Crumb, CrumbTextProps } from "./types"

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
`

interface IconWrapperProps {
  margin: string
}
const IconWrapper = styled.div<IconWrapperProps>`
  display: flex;
  align-items: center;
  margin-left: ${(props) => props.margin};
  margin-right: ${(props) => props.margin};
`

const CrumbText = styled.div<CrumbTextProps>`
  font-size: ${(props) => props.fontSize};
  color: ${(props) => overloadColor(props.color)};
  cursor: ${(props) => (props.hoverEffect ? "pointer" : "default")};

  a {
    color: inherit;
    text-decoration: none;
  }
`

export const Breadcrumbs = React.forwardRef<HTMLDivElement, BreadcrumbsProps>(
  (
    {
      className,
      id,
      crumbs,
      overrides = {},

      color = "inherit",
      fontSize = "inherit",
      iconProps = {},
      iconMargins = "16px",
    },
    ref,
  ) => {
    function handleCrumbClick(
      crumb: Crumb,
      evt: React.MouseEvent<HTMLElement>,
    ) {
      const res = crumb.onClick?.(evt)
      if (evt.defaultPrevented) return false
      return res
    }

    function linkWrap(crumb: Crumb) {
      if (isUndefined(crumb.href))
        return (
          <span onClick={(e) => handleCrumbClick(crumb, e)}>{crumb.text}</span>
        )
      return (
        <Link to={crumb.href} onClick={(e) => handleCrumbClick(crumb, e)}>
          {crumb.text}
        </Link>
      )
    }
    const overriddenCrumbs = useMemo<RequiredSome<Crumb, "id">[]>(
      () =>
        crumbs.reduce<RequiredSome<Crumb, "id">[]>((acc, crumb) => {
          const override: string | null | undefined = overrides[crumb.text]
          if (override === null) return acc

          return [
            ...acc,
            {
              ...crumb,
              id: crumb.id ?? uniqueId("crumb"),
              text: override ?? crumb.text,
            },
          ]
        }, []),
      [crumbs, overrides],
    )

    return (
      <Wrapper ref={ref} className={className} id={id}>
        {overriddenCrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.id}>
            {!!index && (
              <IconWrapper margin={iconMargins}>
                <Icon icon="chevron-right" {...iconProps} />
              </IconWrapper>
            )}
            <CrumbText
              className={crumb.className}
              hoverEffect={
                !isUndefined(crumb.onClick) || !isUndefined(crumb.href)
              }
              color={crumb.color ?? color}
              fontSize={fontSize}
              onClick={(evt) => handleCrumbClick(crumb, evt)}
            >
              {linkWrap(crumb)}
            </CrumbText>
          </React.Fragment>
        ))}
      </Wrapper>
    )
  },
)
